import { useEffect, useRef, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { registerSW } from 'virtual:pwa-register';
import { useToast } from '../../contexts/ToastContext';
import { GIT_SHA, getFullVersionString } from '../../utils/version';
import { isSameKnownBuild } from '../../utils/pwaBuildIdentity';

interface BuildMetadata { gitSha?: string }

async function fetchLatestBuildSha(): Promise<string | undefined> {
  const response = await fetch(`/build-meta.json?t=${Date.now()}`, { cache: 'no-store' });
  if (!response.ok) return undefined;
  const metadata = await response.json() as BuildMetadata;
  return metadata.gitSha;
}

export function PWAUpdatePrompt() {
  const { showWarning } = useToast();
  const [needRefresh, setNeedRefresh] = useState(false);
  const [updateSW, setUpdateSW] = useState<((reloadPage?: boolean) => Promise<void>) | null>(null);
  const promptedBuildRef = useRef<string | null>(null);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const promptForUpdate = (latestSha?: string) => {
      const buildKey = latestSha?.toLowerCase() ?? 'service-worker-update';
      if (promptedBuildRef.current === buildKey) return;
      promptedBuildRef.current = buildKey;
      setNeedRefresh(true);
      showWarning('A newer platform build is ready. Reload when your current task is safe.', 0);
    };

    let updateInterval: number | undefined;
    const runUpdate = registerSW({
      immediate: true,
      onNeedRefresh() {
        void (async () => {
          const latestSha = await fetchLatestBuildSha().catch(() => undefined);
          if (isSameKnownBuild(GIT_SHA, latestSha)) {
            // Multiple Vercel integrations can build the same commit more than
            // once. Leave that equivalent worker waiting: vite-plugin-pwa 0.20.5
            // ignores updateSW(false), activates it, then reloads on control.
            return;
          }

          promptForUpdate(latestSha);
        })();
      },
      onRegisteredSW(_swScriptUrl, registration) {
        if (!registration) return;
        const checkForUpdate = async () => {
        registrationRef.current = registration;
          await registration.update().catch(() => undefined);
          const latestSha = await fetchLatestBuildSha().catch(() => undefined);
          if (GIT_SHA !== 'unknown' && latestSha && !isSameKnownBuild(GIT_SHA, latestSha)) {
            promptForUpdate(latestSha);
          }
        };

        void checkForUpdate();
        // Poll for a freshly-deployed SW every 60s. Without this, an open tab
        // only checks for updates on navigation, so a new deploy stays invisible
        // (and keeps serving the old bundle) until the tab navigates or closes —
        // the "waiting SW" trap. With the poll, onNeedRefresh fires within ~60s
        // of a deploy. The independent metadata check preserves the operator
        // prompt independently of the worker lifecycle event ordering.
        updateInterval = window.setInterval(() => { void checkForUpdate(); }, 60_000);
      },
      onRegisterError(error) {
        console.warn('[PWAUpdatePrompt] Service worker registration failed:', error);
      },
    });

    setUpdateSW(() => runUpdate);
    return () => {
      if (updateInterval) window.clearInterval(updateInterval);
    };
  }, [showWarning]);


  const reloadSafely = async () => {
    const registration = registrationRef.current;
    await registration?.update().catch(() => undefined);

    if (registration?.waiting && updateSW) {
      await updateSW(true);
      return;
    }

    window.location.reload();
  };
  if (!needRefresh) return null;

  return (
    <div className="fixed bottom-4 left-4 z-50 max-w-sm rounded-lg border border-amber-300 bg-white shadow-lg p-3 text-sm text-slate-800">
      <div className="flex items-start gap-3">
        <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-900">New build ready</div>
          <div className="mt-0.5 text-xs text-slate-600">
            Reload to use the latest platform fixes. Current build: {getFullVersionString()}.
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => void reloadSafely()}
              className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Reload
            </button>
            <button
              type="button"
              onClick={() => setNeedRefresh(false)}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              <X className="h-3.5 w-3.5" />
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
